/*
 * PlayerAvatar — circular avatar for a player.
 *
 * If avatarUrl is set, render the photo inside a round mask. Otherwise
 * fall back to a single-letter monogram drawn on a muted background so
 * roster rows always have a left-anchored identity element.
 *
 * Sized in pixels. Pairs well with TeamAvatar — same shape, similar
 * visual weight — so a player row can sit between a team header and an
 * action button without anything feeling out of scale.
 */

interface Props {
  avatarUrl?: string | null;
  displayName?: string | null;
  size?: number;
}

export function PlayerAvatar({ avatarUrl, displayName, size = 24 }: Props) {
  const radius = size / 2;

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={displayName ?? 'Player avatar'}
        width={size}
        height={size}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          objectFit: 'cover',
          flexShrink: 0,
          background: '#0a0a0a',
        }}
      />
    );
  }

  const initial = (displayName ?? '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: radius,
        background: 'var(--input)',
        border: '1px solid var(--border)',
        color: 'var(--text-sec)',
        fontSize: Math.max(10, Math.round(size * 0.45)),
        fontWeight: 800,
        lineHeight: 1,
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {initial}
    </span>
  );
}
